// Client layer (TS-2.5.03). Renders an ImportPreview (tab/row counts, unmapped column bindings,
// and warnings - which include the D-195 contested-53 duplicate finding when the workbook produces
// one) and gates commit behind an explicit "I have reviewed the warnings" checkbox whenever there
// are any warnings to review - commit itself still succeeds without it only when there is nothing
// to accept (decisions.md: a warning is informational, never a silent auto-accept).
import { useState } from "react";
import type { ImportPreview } from "../../api/generated/contracts";
import { strings } from "../../strings";

export interface ImportPreviewPanelProps {
  readonly preview: ImportPreview;
  readonly committing: boolean;
  readonly onCommit: (acceptWarnings: boolean) => void;
}

const S = strings.import.preview;

export function ImportPreviewPanel({ preview, committing, onCommit }: ImportPreviewPanelProps) {
  const [accepted, setAccepted] = useState(false);
  const hasWarnings = preview.warnings.length > 0;
  const unmapped = preview.columnBindings.filter((binding) => binding.targetField === null);

  return (
    <section className="eg-import-preview" aria-labelledby="eg-import-preview-heading">
      <h2 id="eg-import-preview-heading">{S.heading}</h2>

      <h3>{S.tabsFound}</h3>
      <ul>
        {preview.tabsFound.map((tab) => (
          <li key={tab}>{tab}</li>
        ))}
      </ul>

      <h3>{S.rowCounts}</h3>
      <dl>
        {Object.entries(preview.rowCountsByTab).map(([tab, count]) => (
          <div key={tab}>
            <dt>{tab}</dt>
            <dd>{count}</dd>
          </div>
        ))}
      </dl>

      {unmapped.length > 0 && (
        <>
          <h3>{S.unmapped}</h3>
          <ul>
            {unmapped.map((binding) => (
              <li key={binding.columnIndex}>{binding.header}</li>
            ))}
          </ul>
        </>
      )}

      {hasWarnings && (
        <>
          <h3>{S.warnings}</h3>
          <ul>
            {preview.warnings.map((warning, index) => (
              <li key={`${warning.code}-${warning.entityKey ?? index}`}>
                [{warning.severity}] {warning.message}
              </li>
            ))}
          </ul>
          <label>
            <input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} />
            {S.accept}
          </label>
        </>
      )}

      <button type="button" disabled={committing || (hasWarnings && !accepted)} onClick={() => onCommit(accepted)}>
        {committing ? S.committing : S.commit}
      </button>
      {hasWarnings && !accepted && <p>{S.commitDisabledHint}</p>}
    </section>
  );
}
